// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  black,
  gray01,
  gray02,
  gray03,
  gray05,
  white,
} from 'presentations/views/styles/color';
import Avatar from 'presentations/views/components/atoms/Avatar';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Icon from 'presentations/views/components/atoms/Icon';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Emoji from 'presentations/views/components/atoms/Emoji';
import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { UserListItem } from 'presentations/api/schema/user';
import Menu from 'presentations/views/components/atoms/Menu';
import Progress from 'presentations/views/components/atoms/Progress';
import Link from 'presentations/views/components/atoms/Link';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  isInElectron,
  isiOSApp as isiOSAppFunc,
} from 'presentations/views/helpers/electron';
import Tooltip from 'presentations/views/components/atoms/Tooltip';

interface DashboardHeaderMenu {
  icon: string;
  name: string;
  onClick: () => void;
}

interface Props {
  title?: string | null;
  description?: string | null;
  iconName?: string;
  titleIconSuffix?: string | null;
  emojiIconName?: string;
  iconColor?: 'inherit' | 'primary';
  users?: UserListItem[] | null;
  onClickIcon?: () => void;
  onClickUserList?: () => void;
  onClickSettings?: () => void;
  onClickDeleteProject?: () => void;
  onClickInvite?: () => void;
  isLoading?: boolean;
  isContentLoading?: boolean;
  onClickBack?: () => void;
  returnUrl?: string;
  subContent?: ReactNode;
  isHideMenu?: boolean;
  menuOptions?: DashboardHeaderMenu[];
  titleTooltip?: string;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentUrl, setCurrentUrl] = useState<string>('');

  const isiOSApp = isiOSAppFunc();

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const isMobile = isMediaMax('tablet');
  const maxUserCount = isMobile ? 4 : 6;
  const menuOptions = (props.menuOptions || []).map((menuOption, index) => (
    <MenuItem key={index}>
      <MenuIcon name={menuOption.icon} />
      <MenuItemName>{menuOption.name}</MenuItemName>
    </MenuItem>
  ));
  const isElectron = isInElectron();
  if (isElectron) {
    menuOptions.push(
      <CopyMenuItem>
        <CopyToClipboard text={currentUrl} onCopy={onCopyClipboard}>
          <MenuItem>
            <MenuIcon name="file_copy_outlined" />
            <MenuItemName>{t('Copy Link')}</MenuItemName>
          </MenuItem>
        </CopyToClipboard>
      </CopyMenuItem>,
    );
  }

  const projectMenuOptions = [
    <MenuItem>
      <MenuIcon name="folder-edit-outline" />
      <MenuItemName>{t('Edit')}</MenuItemName>
    </MenuItem>,
    <MenuItem>
      <MenuIcon name="delete" />
      <MenuItemName>{t('Delete')}</MenuItemName>
    </MenuItem>,
  ];

  const [
    projectMenuAnchor,
    setProjectMenuAnchor,
  ] = useState<HTMLElement | null>(null);
  const onClickProjectSettings = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setProjectMenuAnchor(e.currentTarget);
  };
  const onSelectProjectMenu = (index: number) => {
    if (index === 0 && props.onClickSettings) {
      props.onClickSettings();
    } else if (index === 1 && props.onClickDeleteProject) {
      props.onClickDeleteProject();
    }
    onCloseProjectMenu();
  };
  const onCloseProjectMenu = () => {
    setProjectMenuAnchor(null);
  };

  function onCopyClipboard() {}

  function onSelectMenu(selectedIndex: number) {
    setMenuAnchor(null);
    if (!props.menuOptions || !props.menuOptions[selectedIndex]) {
      return;
    }
    const menuOption = props.menuOptions[selectedIndex];
    if (menuOption.onClick) menuOption.onClick();
  }
  function onCloseMenu(e: SyntheticEvent<HTMLButtonElement>) {
    setMenuAnchor(null);
  }

  return (
    <>
      <Container>
        {(props.returnUrl || props.onClickBack) && !isiOSApp && (
          <BackLink href={props.returnUrl} onClick={props.onClickBack}>
            <BackIcon name="chevron-left" color="primary" />
          </BackLink>
        )}
        <Content>
          {(props.iconName || props.emojiIconName) && (
            <EmojiContainer onClick={props.onClickIcon}>
              {props.emojiIconName && (
                <Emoji name={props.emojiIconName || 'flag'} size={16} />
              )}
              {props.iconName && (
                <MenuIcon
                  name={props.iconName || ''}
                  color={props.iconColor || 'primary'}
                />
              )}
            </EmojiContainer>
          )}
          {props.isLoading ? (
            <Skeleton width="100px" height="32px" />
          ) : (
            <TitleContainer>
              {props.title && (
                <MenuTitle>
                  <MenuTitleText>{props.title}</MenuTitleText>
                  {props.titleIconSuffix && (
                    <MenuTitleIconSuffix name={props.titleIconSuffix} />
                  )}
                  {props.titleTooltip && (
                    <InfoIconContainer>
                      <Tooltip title={t(props.titleTooltip)} placement="top">
                        <InfoIcon name="information" color="inherit" />
                      </Tooltip>
                    </InfoIconContainer>
                  )}
                </MenuTitle>
              )}
              {props.description && (
                <Description>{props.description}</Description>
              )}
            </TitleContainer>
          )}
          {props.isContentLoading && (
            <ContentLoading>
              <Progress size={12} thickness={4} color="inherit" />
            </ContentLoading>
          )}
        </Content>
        <RightContent>
          {props.users && (
            <>
              <UsersContainer onClick={props.onClickUserList}>
                {props.users
                  .slice(
                    0,
                    props.users.length === maxUserCount
                      ? maxUserCount
                      : maxUserCount - 1,
                  )
                  .map((user) => (
                    <UserAvatar
                      // TODO color
                      src={user.profileImage}
                      key={user.uid}
                    >
                      {user.name.charAt(0)}
                    </UserAvatar>
                  ))}
                {props.users.length > maxUserCount && (
                  <MoreUser>+{props.users.length - maxUserCount + 1}</MoreUser>
                )}
              </UsersContainer>
              {props.isLoading && <Skeleton width="100px" height="32px" />}
              {props.onClickInvite && (
                <MenuIconButton onClick={props.onClickInvite}>
                  <ButtonIcon name="account-plus" />
                </MenuIconButton>
              )}
            </>
          )}
          {props.subContent && <RightContent>{props.subContent}</RightContent>}
          {props.onClickSettings && (
            <MenuIconButton onClick={onClickProjectSettings}>
              <ButtonIcon name="dots-vertical" />
            </MenuIconButton>
          )}
        </RightContent>
      </Container>
      {menuOptions.length > 0 && (
        <Menu
          onClose={onCloseMenu}
          onSelect={onSelectMenu}
          anchor={menuAnchor}
          options={menuOptions}
        />
      )}
      {projectMenuAnchor && (
        <Menu
          onClose={onCloseProjectMenu}
          onSelect={onSelectProjectMenu}
          anchor={projectMenuAnchor}
          options={projectMenuOptions}
        />
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 62px;
  padding: 0 ${spacing(24)};
  align-items: center;
  border-bottom: 1px solid ${gray05};
  background-color: ${white};

  ${mediaMax.smartphone} {
    height: 37px;
    padding: 0 ${spacing(12)};
  }
`;
const TitleContainer = styled.div``;
const MenuIcon = styled(Icon)`
  width: 24px !important;
  height: 24px !important;
  margin-right: ${spacing(8)};

  ${mediaMax.smartphone} {
    width: 17px !important;
    height: 17px !important;
    margin-right: ${spacing(6)};
  }
`;
const ContentLoading = styled.div`
  margin-left: ${spacing(8)};
  color: ${gray02};
`;
const EmojiContainer = styled.div<{ onClick?: Function }>`
  display: flex;
  margin-right: ${spacing(4)};

  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
    `}
`;
const MenuIconButton = styled(IconButton)`
  ${mediaMax.smartphone} {
    padding: ${spacing(6)};
  }
`;
const MenuTitle = styled.div`
  font-size: ${fontSize(16)};
  color: ${black};
  font-weight: ${fontWeight('bold')};
  display: flex;
  align-items: center;

  ${mediaMax.smartphone} {
    font-size: ${fontSize(12)};
  }
`;
const MenuTitleText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
  ${mediaMax.smartphone} {
    max-width: 140px;
  }
`;

const MenuTitleIconSuffix = styled(Icon)`
  font-size: ${fontSize(16)};
  color: ${gray01};
  margin-left: ${spacing(8)};

  ${mediaMax.smartphone} {
    font-size: ${fontSize(14)};
  }
`;

const RightContent = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-grow: 1;
`;
const UsersContainer = styled.div`
  display: flex;
  cursor: pointer;
  margin-right: ${spacing(8)};

  ${mediaMax.smartphone} {
    margin-right: ${spacing(4)};
  }
`;
const UserAvatar = styled(Avatar)<{ color?: string }>`
  position: relative;
  width: 32px;
  height: 32px;
  margin-left: -4px;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(13)};
  background-color: ${(props) => props.color};

  ${mediaMax.tablet} {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(12)};
  }
`;
const MoreUser = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
  margin-left: -4px;
  border-radius: ${borderRadius(16)};
  background-color: ${gray03};
  line-height: 32px;
  text-align: center;
  font-size: ${fontSize(12)};
  color: ${white};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.smartphone} {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};
    line-height: 20px;
  }
`;
const ButtonIcon = styled(Icon)`
  width: 24px !important;
  height: 24px !important;
  color: ${gray01};

  ${mediaMax.smartphone} {
    width: 20px !important;
    height: 20px !important;
  }
`;

const BackIcon = styled(Icon)`
  font-size: ${fontSize(10)};
  margin-right: ${spacing(16)};
`;
const BackLink = styled(Link)`
  color: ${black};
  display: flex;
  align-items: center;
`;
const Content = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;
const Description = styled.div`
  ${mediaMax.smartphone} {
    display: none;
  }
`;

const CopyMenuItem = styled.div``;
const MenuItem = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
`;
const MenuItemName = styled.div`
  font-weight: ${fontWeight('bold')};
`;

const InfoIconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: ${spacing(4)};
  line-height: 13px;
  vertical-align: middle;
`;
const InfoIcon = styled(Icon)`
  font-size: ${fontSize(16)};
  color: ${gray02};
`;
